import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { resetPin, checkBothPins } from '../services/api';
import './PinReset.css';

function PinReset() {
  const [formData, setFormData] = useState({
    username: localStorage.getItem('username') || '',
    password: '',
    newPin: '',
    confirmPin: '',
    gender: 'male' // default
  });
  
  const [pinStatus, setPinStatus] = useState({
    hasMalePin: false,
    hasFemalePin: false
  });
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingPins, setCheckingPins] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // If no username in localStorage, redirect to login
    const username = localStorage.getItem('username');
    if (!username) {
      navigate('/login');
      return;
    }
    
    // Check which PINs exist
    checkExistingPins(username);
  }, [navigate]);

  const checkExistingPins = async (username) => {
    try {
      setCheckingPins(true);
      const status = await checkBothPins(username);
      setPinStatus(status);
    } catch (err) {
      console.error('Error checking PINs:', err);
    } finally {
      setCheckingPins(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Limit PIN fields to 4 digits
    if ((name === 'newPin' || name === 'confirmPin')) {
      if (value.length > 4) return;
      if (!/^\d*$/.test(value)) return;
    }
    
    setFormData({ ...formData, [name]: value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.username || !formData.password) {
      setError('Username and password are required');
      return;
    }

    if (formData.newPin.length !== 4) {
      setError('PIN must be exactly 4 digits');
      return;
    }

    if (formData.newPin !== formData.confirmPin) {
      setError('PINs do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await resetPin(
        formData.username,
        formData.password,
        formData.newPin,
        formData.gender
      );

      if (response.success) {
        const genderLabel = formData.gender === 'male' ? 'Male' : 'Female';
        setSuccess(`${genderLabel} partner PIN reset successfully!`);
        
        // Update PIN status
        await checkExistingPins(formData.username);
        
        // Clear form
        setFormData({
          ...formData,
          password: '',
          newPin: '',
          confirmPin: ''
        });

        // Redirect after 2 seconds
        setTimeout(() => {
          navigate('/profile');
        }, 2000);
      } else {
        setError(response.message || 'PIN reset failed');
      }

    } catch (err) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Incorrect password or username. Please try again.');
      }
      console.error('PIN reset error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1); // Go back to previous page
  };

  if (checkingPins) {
    return (
      <div className="pin-reset-container">
        <div className="pin-reset-card">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Checking PIN status...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pin-reset-container">
      <div className="pin-reset-card">
        <div className="pin-reset-header">
          <h2>Reset Your PIN</h2>
          <p className="subtitle">Verify your password to set a new PIN</p>
        </div>

        {/* PIN Status Display */}
        <div className="pin-status-section">
          <h3>Current PIN Status</h3>
          <div className="pin-status-grid">
            <div className={`pin-status-item ${pinStatus.hasMalePin ? 'has-pin' : 'no-pin'}`}>
              <span className="status-icon">♂️</span>
              <span className="status-label">Male PIN</span>
              <span className="status-indicator">
                {pinStatus.hasMalePin ? '✓ Set' : '✗ Not Set'}
              </span>
            </div>
            <div className={`pin-status-item ${pinStatus.hasFemalePin ? 'has-pin' : 'no-pin'}`}>
              <span className="status-icon">♀️</span>
              <span className="status-label">Female PIN</span>
              <span className="status-indicator">
                {pinStatus.hasFemalePin ? '✓ Set' : '✗ Not Set'}
              </span>
            </div>
          </div>
        </div>

        {error && <div className="error-message" role="alert">{error}</div>}
        {success && <div className="success-message" role="alert">{success}</div>}

        <form onSubmit={handleSubmit}>
          {/* Username */}
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="Your username"
              required
              readOnly
              className="readonly-input"
            />
          </div>

          {/* Password */}
          <div className="form-group">
            <label htmlFor="password">
              Password
              <span className="field-hint">Verify your account</span>
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              required
              autoComplete="current-password"
            />
          </div>

          {/* Gender Selection */}
          <div className="form-group">
            <label>Which PIN do you want to reset?</label>
            <div className="gender-select-group">
              <label className={`gender-option ${formData.gender === 'male' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="gender"
                  value="male"
                  checked={formData.gender === 'male'}
                  onChange={handleChange}
                />
                <span className="gender-label">
                  <span className="gender-icon">♂️</span>
                  Male Partner PIN
                  {pinStatus.hasMalePin && <span className="current-badge">Current</span>}
                </span>
              </label>
              <label className={`gender-option ${formData.gender === 'female' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="gender"
                  value="female"
                  checked={formData.gender === 'female'}
                  onChange={handleChange}
                />
                <span className="gender-label">
                  <span className="gender-icon">♀️</span>
                  Female Partner PIN
                  {pinStatus.hasFemalePin && <span className="current-badge">Current</span>}
                </span>
              </label>
            </div>
          </div>

          {/* New PIN */}
          <div className="form-group">
            <label htmlFor="newPin">New PIN</label>
            <input
              type="password"
              id="newPin"
              name="newPin"
              value={formData.newPin}
              onChange={handleChange}
              placeholder="4-digit PIN"
              maxLength="4"
              pattern="\d{4}"
              className="pin-input"
              required
              inputMode="numeric"
            />
            <small className="pin-info">
              {formData.newPin.length > 0 ? (
                <span className="pin-progress">
                  {formData.newPin.length}/4 digits
                  {formData.newPin.length === 4 && <span className="pin-complete"> ✓</span>}
                </span>
              ) : (
                <span>Enter 4-digit PIN</span>
              )}
            </small>
          </div>

          {/* Confirm PIN */}
          <div className="form-group">
            <label htmlFor="confirmPin">Confirm New PIN</label>
            <input
              type="password"
              id="confirmPin"
              name="confirmPin"
              value={formData.confirmPin}
              onChange={handleChange}
              placeholder="Re-enter PIN"
              maxLength="4"
              pattern="\d{4}"
              className="pin-input"
              required
              inputMode="numeric"
            />
            <small className="pin-info">
              {formData.confirmPin.length === 4 && (
                formData.newPin === formData.confirmPin ? (
                  <span className="pin-match">✓ PINs match</span>
                ) : (
                  <span className="pin-mismatch">✗ PINs don't match</span>
                )
              )}
            </small>
          </div>

          {/* Buttons */}
          <div className="button-group">
            <button 
              type="submit" 
              className="reset-button"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-small"></span>
                  Resetting PIN...
                </>
              ) : (
                'Reset PIN'
              )}
            </button>
            
            <button 
              type="button" 
              className="cancel-button"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>

        <div className="security-notice">
          <p>🔒 <strong>Security Note:</strong> Your password is required to verify your identity before resetting your PIN.</p>
        </div>
      </div>
    </div>
  );
}

export default PinReset;